import React, { useState } from 'react';
import {
  FormControl,
  MenuItem,
  Popover,
  Select,
  TextField,
  IconButton,
  Tooltip,
} from '@mui/material';
import SliderWithInput from '../../SliderWithInput';
import { textFieldStyleProps } from '../GridAndAxes';
import { ColorResult, SketchPicker } from 'react-color';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch } from 'react-redux';
import { updateAxisOptions } from '../../../../redux/ChartPoperties/ChartControlsActions';
import InputNumber from '../../CommonFunctions/InputNumber';
import YAxisFormat from '../../Format/YAxisFormat';

interface PositionsProps {
  name?: string;
  value: string;
  type?: string;
}

interface SeriesAxisOptionsProps {
  propKey: string;
  seriesNumber: string;
  axisType: 'x' | 'y';
  axisProps: any;
  positions: PositionsProps[];
  chartType?: string;
  iconColor?: string;
  backgroundColor?: string;
}

/**
 * This component is rendered by MultipleSeries.tsx and AxisOptions.tsx.
 * @returns Axis options UI with functionality inside series i.e manual min/max inputs (conditionally), axis name section and tick section.
 * min/max will appear inside series only in case of multibar, horizontal bar and line chart. For others, this min/max before axis setting.
 */
const SeriesAxisOptions: React.FC<SeriesAxisOptionsProps> = ({
  propKey,
  seriesNumber,
  axisType,
  axisProps,
  positions,
  chartType,
  iconColor,
  backgroundColor,
}) => {
  const dispatch = useDispatch();

  const [isColorPopoverOpen, setColorPopoverOpen] = useState<boolean>(false);
  const [isTickExpanded, setTickExpanded] = useState<boolean>(false);
  const [isNameExpanded, setNameExpanded] = useState<boolean>(false);
  const [isFormatExpanded, setIsFormatExpanded] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // --- VALIDATION LOGIC ---
  const minMaxError =
    axisProps.enableMin &&
    axisProps.enableMax &&
    Number(axisProps.minValue ?? 0) >= Number(axisProps.maxValue ?? 10000);

  const handleColorClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setColorPopoverOpen(true);
  };

  const handleColorClose = () => {
    setAnchorEl(null);
    setColorPopoverOpen(false);
  };

  const axisKey = `${axisType}Axis`;

  const updateSeriesAxisOption = (option: string, value: any) => {
    dispatch(updateAxisOptions(propKey, axisKey, option, value, seriesNumber));
  };

  const getTickProperty = (propName: string) => {
    const position = axisProps.position;
    return axisProps[`${propName}${position.charAt(0).toUpperCase() + position.slice(1)}`];
  };

  const updateTickProperty = (propName: string, value: any) => {
    const position = axisProps.position;
    updateSeriesAxisOption(
      `${propName}${position.charAt(0).toUpperCase() + position.slice(1)}`,
      value,
    );
  };

  const shouldShowMinMax = () => {
    if (!chartType) return false;

    // For horizontal bar charts, show min/max in x-axis
    if (['horizontalBar'].includes(chartType) && axisType === 'x') {
      return true;
    }

    // For other chart types, show min/max in y-axis
    if (['line', 'area', 'multibar'].includes(chartType) && axisType === 'y') {
      return true;
    }

    return false;
  };

  return (
    <div style={{ marginTop: '10px', marginBottom: '10px' }}>
      {/* Min/Max Values Section */}
      {shouldShowMinMax() && (
        <>
          <div
            className="optionDescription"
            style={{
              marginTop: '-14px',
              marginBottom: '-7px',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Range(Data Points)
          </div>
          <div style={{ transform: 'TranslateY(-3px)' }}>
            <div
              className="optionDescription"
              style={{ marginBottom: '7px', color: iconColor ? iconColor : '#404040' }}
            >
              Min Value
            </div>
            <div
              className="optionDescription"
              style={{ transform: 'TranslateY(-15px)', color: iconColor ? iconColor : '#404040' }}
            >
              <input
                type="checkbox"
                style={{ width: '16px', height: '16px' }}
                checked={axisProps.enableMin ?? false}
                onChange={() => {
                  updateSeriesAxisOption('enableMin', !axisProps.enableMin);
                }}
              />
              <InputNumber
                value={axisProps.minValue ?? 0}
                updateValue={(value: number) => {
                  updateSeriesAxisOption('minValue', value);
                }}
                disabled={!axisProps.enableMin}
              />
            </div>
            <div style={{ transform: 'TranslateY(-10px)' }}>
              <div
                className="optionDescription"
                style={{
                  marginTop: '0',
                  marginBottom: '7px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Max Value
              </div>
              <div
                className="optionDescription"
                style={{
                  transform: 'TranslateY(-15px)',
                  marginBottom: '-9px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={axisProps.enableMax ?? false}
                  onChange={() => {
                    updateSeriesAxisOption('enableMax', !axisProps.enableMax);
                  }}
                />
                <InputNumber
                  value={axisProps.maxValue ?? 10000}
                  updateValue={(value: number) => {
                    updateSeriesAxisOption('maxValue', value);
                  }}
                  disabled={!axisProps.enableMax}
                />
              </div>
              {minMaxError && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '0px',
                    marginBottom: '15px',
                    textAlign: 'left',
                    paddingLeft: '10px',
                  }}
                >
                  Max value should be greater than Min Value
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/*  format section with Expand/Collapse icon*/}
      {shouldShowMinMax() && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              userSelect: 'none',
              marginBottom: isFormatExpanded ? '-5px' : '12px',
              marginLeft: '2px',
              marginTop: '-10px',
            }}
            onClick={() => setIsFormatExpanded((prev) => !prev)}
            aria-label={isFormatExpanded ? 'Collapse Format Controls' : 'Expand Format Controls'}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsFormatExpanded((prev) => !prev);
              }
            }}
          >
            <div
              className="optionDescription"
              style={{
                paddingLeft: '0.4rem',
                paddingTop: '0',
                marginTop: '-10px',
                color: iconColor ? iconColor : '#404040',
              }}
            >
              Format
            </div>
            <Tooltip
              title={isFormatExpanded ? 'Click to Collapse' : 'Click to Expand'}
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: '12px',
                  },
                },
              }}
            >
              <IconButton
                size="small"
                disableRipple
                disableFocusRipple
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFormatExpanded((prev) => !prev);
                }}
                aria-label={
                  isFormatExpanded ? 'Collapse Axis Name Controls' : 'Expand Axis Name Controls'
                }
                sx={{
                  cursor: 'default',
                  paddingTop: '0',
                  transform: 'TranslateY(-5px)',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                  '&:focus, &:active': {
                    backgroundColor: 'transparent',
                    outline: 'none',
                  },
                }}
              >
                {isFormatExpanded ? (
                  <KeyboardArrowDownIcon
                    fontSize="medium"
                    sx={{ color: iconColor ? iconColor : '#404040' }}
                  />
                ) : (
                  <ChevronRightIcon
                    fontSize="medium"
                    sx={{ color: iconColor ? iconColor : '#404040' }}
                  />
                )}
              </IconButton>
            </Tooltip>
          </div>
          {isFormatExpanded && (
            <YAxisFormat
              seriesNumber={seriesNumber}
              from="multi-series"
              iconColor={iconColor}
              backgroundColor={backgroundColor}
            />
          )}
        </>
      )}

      {/* Axis Name Section */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: isNameExpanded ? '-5px' : '-10px',
          marginLeft: '2px',
          marginTop: isFormatExpanded ? '15px' : '',
        }}
        onClick={() => setNameExpanded((prev) => !prev)}
        aria-label={isNameExpanded ? 'Collapse Axis Name Controls' : 'Expand Axis Name Controls'}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setNameExpanded((prev) => !prev);
          }
        }}
      >
        <div
          className="optionDescription"
          style={{
            paddingLeft: '0.4rem',
            paddingTop: '0',
            marginTop: '-10px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Axis Name
        </div>
        <Tooltip
          title={isNameExpanded ? 'Click to Collapse' : 'Click to Expand'}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            disableRipple
            disableFocusRipple
            onClick={(e) => {
              e.stopPropagation();
              setNameExpanded((prev) => !prev);
            }}
            aria-label={
              isNameExpanded ? 'Collapse Axis Name Controls' : 'Expand Axis Name Controls'
            }
            sx={{
              cursor: 'default',
              paddingTop: '0',
              transform: 'TranslateY(-5px)',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:focus, &:active': {
                backgroundColor: 'transparent',
                outline: 'none',
              },
            }}
          >
            {isNameExpanded ? (
              <KeyboardArrowDownIcon
                fontSize="medium"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <ChevronRightIcon
                fontSize="medium"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </Tooltip>
      </div>
      {isNameExpanded && (
        <>
          <TextField
            value={axisProps.name || ''}
            variant="outlined"
            onChange={(e) => updateSeriesAxisOption('name', e.target.value)}
            sx={{ width: '100%', transform: 'TranslateX(-2px)' }}
            InputProps={{
              ...textFieldStyleProps,
              sx: {
                color: iconColor ? iconColor : '#404040',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
              },
            }}
          />

          <div
            className="optionDescription"
            style={{
              paddingLeft: '0.4rem',
              paddingTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Name Position
          </div>
          <FormControl fullWidth size="small" style={{ fontSize: '12px', borderRadius: '4px' }}>
            <Select
              value={axisProps.nameLocation || 'middle'}
              variant="outlined"
              onChange={(e) => updateSeriesAxisOption('nameLocation', e.target.value)}
              sx={{
                fontSize: '12px',
                width: '95%',
                margin: '0 auto 0.5rem auto',
                backgroundColor: 'white',
                height: '1.5rem',
                color: '#404040',
                textAlign: 'left',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: 'rgba(43, 185, 187, 0.1) !important',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: 'rgba(43, 185, 187, 0.2) !important',
                    },
                  },
                },
              }}
            >
              {positions.map((position) => (
                <MenuItem
                  value={position.value}
                  key={position.name}
                  sx={{ padding: '2px 10px', fontSize: '12px' }}
                >
                  {position.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div
            className="optionDescription"
            style={{
              paddingLeft: '0.4rem',
              paddingTop: '0',
              color: iconColor ? iconColor : '#404040',
            }}
          >
            Name Gap
          </div>
          <TextField
            value={axisProps.nameGap || 0}
            variant="outlined"
            onChange={(e) => updateSeriesAxisOption('nameGap', e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              ...textFieldStyleProps,
              sx: {
                color: iconColor ? iconColor : '#404040',
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#2bb9bb',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'lightgray',
                },
              },
            }}
          />

          <div
            className="optionDescription"
            style={{ paddingTop: '0', color: iconColor ? iconColor : '#404040' }}
          >
            Name Font Size
          </div>
          <SliderWithInput
            percent={true}
            sliderValue={axisProps.nameSize || 12}
            sliderMinMax={{ min: 0, max: 80, step: 1 }}
            changeValue={(value: number) => {
              updateSeriesAxisOption('nameSize', value);
            }}
          />

          <div
            style={{
              display: 'flex',
              marginTop: '2px',
              marginBottom: '2px',
              marginRight: '2px',
            }}
          >
            <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
              Name Color
            </div>
          </div>
          <div
            style={{
              width: '25%',
              height: '1.25rem',
              color: axisProps.nameColor || '#000000',
              border: '1px solid lightgray',
              borderRadius: '3px',
              padding: '0 5px',
              marginLeft: '5px',
              backgroundColor: axisProps.nameColor || '#000000',
              alignSelf: 'end',
            }}
            onClick={handleColorClick}
          />
          <Popover
            open={isColorPopoverOpen}
            anchorReference="anchorEl"
            anchorEl={anchorEl}
            onClose={handleColorClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                boxShadow: 'none',
                border: '1px solid #ddd',
                borderRadius: '6px',
                backgroundColor: 'white',
              },
            }}
          >
            <div style={{ padding: '0' }}>
              <SketchPicker
                color={axisProps.nameColor || '#000000'}
                onChange={(color: ColorResult) => {
                  updateSeriesAxisOption('nameColor', color.hex);
                }}
                disableAlpha
                width="13rem"
                styles={{
                  default: {
                    picker: {
                      boxShadow: 'none',
                      borderRadius: '6px',
                      width: '13rem',
                    },
                  },
                }}
              />
            </div>
          </Popover>
        </>
      )}

      {/* Tick Controls Header with Expand/Collapse */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          marginBottom: isTickExpanded ? '10px' : '15px',
          userSelect: 'none',
        }}
        onClick={() => setTickExpanded((prev: any) => !prev)}
        aria-label={isTickExpanded ? 'Collapse Tick Controls' : 'Expand Tick Controls'}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setTickExpanded((prev) => !prev);
          }
        }}
      >
        <div
          className="optionDescription"
          style={{
            paddingTop: '10px',
            marginRight: '5px',
            color: iconColor ? iconColor : '#404040',
          }}
        >
          Tick
        </div>
        <Tooltip
          title={isTickExpanded ? 'Click to Collapse' : 'Click to Expand'}
          slotProps={{
            tooltip: {
              sx: {
                fontSize: '12px',
              },
            },
          }}
        >
          <IconButton
            size="small"
            disableRipple
            disableFocusRipple
            onClick={(e) => {
              e.stopPropagation();
              setTickExpanded((prev) => !prev);
            }}
            aria-label={isTickExpanded ? 'Collapse Tick Controls' : 'Expand Tick Controls'}
            sx={{
              paddingTop: '18px',
              cursor: 'default',
              '&:hover': {
                backgroundColor: 'transparent',
              },
              '&:focus, &:active': {
                backgroundColor: 'transparent',
                outline: 'none',
              },
            }}
          >
            {isTickExpanded ? (
              <KeyboardArrowDownIcon
                fontSize="medium"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            ) : (
              <ChevronRightIcon
                fontSize="medium"
                sx={{ color: iconColor ? iconColor : '#404040' }}
              />
            )}
          </IconButton>
        </Tooltip>
      </div>
      {isTickExpanded && (
        <>
          <div
            className="optionDescription"
            style={{ paddingTop: '0', color: iconColor ? iconColor : '#404040' }}
          >
            Tick Size
          </div>
          <SliderWithInput
            percent={axisType === 'y'}
            sliderValue={getTickProperty('tickSize') || 5}
            sliderMinMax={{ min: 0, max: 20, step: 1 }}
            changeValue={(value: number) => updateTickProperty('tickSize', value)}
          />

          <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
            Tick Padding
          </div>
          <SliderWithInput
            percent={true}
            sliderValue={getTickProperty('tickPadding') ?? 5}
            sliderMinMax={{ min: 0, max: 20, step: 1 }}
            changeValue={(value: number) => updateTickProperty('tickPadding', value)}
          />
          <div style={{ marginBottom: '30px' }}>
            <div className="optionDescription" style={{ color: iconColor ? iconColor : '#404040' }}>
              Tick Rotation
            </div>
            <SliderWithInput
              degree={true}
              sliderValue={getTickProperty('tickRotation') || 0}
              sliderMinMax={{ min: -90, max: 90, step: 1 }}
              changeValue={(value: number) => updateTickProperty('tickRotation', value)}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default SeriesAxisOptions;
